import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import {
  DefaultService,
  GameOutput,
  LeagueOutput,
  LivePlaysService,
  Player,
  PlayerWithLeagueContext,
  LivePlayInput,
  LivePlayOutput,
  PlayInput,
  LivePlayEndInput,
} from '../../generated-api';
import { RankingEntity } from '../SortableEntity';
import { useData } from '../../contexts/DataContext';
import { useSession } from '../../contexts/SessionContext';
import { useRecentGames } from '../../hooks/useRecentGames';
import { useRecentPlayers } from '../../hooks/useRecentPlayers';
import { useWizardState, WizardState, WizardAction } from '../../hooks/useWizardState';
import { useRankingLogic } from '../../hooks/useRankingLogic';
import WizardNavBar from './WizardNavBar';
import WizardStepContainer from './WizardStepContainer';
import PlayTypeStep from './steps/PlayTypeStep';
import GameSelectionStep from './steps/GameSelectionStep';
import PlayerSelectionStep from './steps/PlayerSelectionStep';
import ConfirmStartStep from './steps/ConfirmStartStep';
import RankingStep from './steps/RankingStep';
import DetailsStep from './steps/DetailsStep';
import TeamAssignmentStep from './steps/TeamAssignmentStep';
import { trackAction } from '../../services/telemetry';

const STEP_TITLES_LIVE: Record<number, string> = {
  1: 'Play Type',
  2: 'Pick a Game',
  3: 'Players',
  4: 'Teams',
  5: 'Review & Start',
};

const STEP_TITLES_RECORD: Record<number, string> = {
  1: 'Play Type',
  2: 'Pick a Game',
  3: 'Players',
  4: 'Teams',
  5: 'Review',
  6: 'Results',
};

// Extracted component for the merged Results step (ranking + collapsible details)
const ResultsStepContent: React.FC<{
  state: WizardState;
  dispatch: React.Dispatch<WizardAction>;
  players: (Player | PlayerWithLeagueContext)[];
  selectedGame: GameOutput | null;
  teamDisplayNames: string[];
  handleRankingEntitiesChange: (updater: (prev: RankingEntity[]) => RankingEntity[]) => void;
  handleTeamMetadataChange: (metadata: Record<string, Record<string, any>>) => void;
}> = ({ state, dispatch, players, selectedGame, teamDisplayNames, handleRankingEntitiesChange, handleTeamMetadataChange }) => {
  const [detailsOpen, setDetailsOpen] = useState(true);

  return (
    <>
      <RankingStep
        rankingEntities={state.rankingEntities}
        onRankingEntitiesChange={handleRankingEntitiesChange}
        teams={state.teams}
        players={players}
      />

      {/* Divider + collapsible details */}
      <div className="mt-4">
        <button
          type="button"
          onClick={() => setDetailsOpen(!detailsOpen)}
          className="flex items-center gap-2 w-full min-h-[44px] sm:min-h-0 group"
        >
          <div className="flex-1 h-px bg-edge" />
          <span className="text-xs font-medium text-muted uppercase tracking-wide whitespace-nowrap
                           group-hover:text-body transition-colors">
            Additional Details
          </span>
          <ChevronDownIcon
            className={`w-4 h-4 text-muted group-hover:text-body transition-transform duration-200
                       ${detailsOpen ? 'rotate-180' : ''}`}
          />
          <div className="flex-1 h-px bg-edge" />
        </button>

        {detailsOpen && (
          <div className="mt-4 animate-fadeIn">
            <DetailsStep
              comment={state.comment}
              onCommentChange={(comment) => dispatch({ type: 'SET_COMMENT', comment })}
              durationMinutes={state.durationMinutes}
              onDurationChange={(minutes) => dispatch({ type: 'SET_DURATION', minutes })}
              playImages={state.playImages}
              onPlayImagesChange={(images) => dispatch({ type: 'SET_PLAY_IMAGES', images })}
              teams={teamDisplayNames}
              teamMetadata={state.teamMetadata}
              onTeamMetadataChange={handleTeamMetadataChange}
              playerMetadataFields={selectedGame?.playerMetadataFields ?? undefined}
              playDate={state.playType === 'record' ? state.playDate : undefined}
              onPlayDateChange={state.playType === 'record' ? (date) => dispatch({ type: 'SET_PLAY_DATE', date }) : undefined}
            />
          </div>
        )}
      </div>
    </>
  );
};

interface PlayWizardProps {
  onClose: () => void;
}

const PlayWizard: React.FC<PlayWizardProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromLivePlayId = searchParams.get('fromLivePlay');
  const rematchPlayId = searchParams.get('rematch');
  const preselectedGameId = searchParams.get('gameId') || undefined;
  const preselectedLeagueId = searchParams.get('leagueId') || undefined;
  const preselectedMode = searchParams.get('mode') === 'live' ? 'live' as const : undefined;

  const { games, leagues, fetchGames, fetchLeagues } = useData();
  const { playerId: currentPlayerId } = useSession();
  const { recentGames, loading: recentGamesLoading } = useRecentGames(6);
  const { recentPlayerIds } = useRecentPlayers(20);
  const { updateRankingEntities } = useRankingLogic();

  const [players, setPlayers] = useState<(Player | PlayerWithLeagueContext)[]>([]);
  const [loading, setLoading] = useState(true);
  const [livePlay, setLivePlay] = useState<LivePlayOutput | null>(null);
  const [gameSpecificLeagues, setGameSpecificLeagues] = useState<LeagueOutput[]>([]);
  const submissionRef = useRef<Promise<unknown> | null>(null);

  const { state, dispatch, isStepValid, clearStorage } = useWizardState({
    preselectedGameId,
    preselectedLeagueId,
    preselectedMode,
  });

  const selectedGame = games.find(g => g.id === state.selectedGameId) || null;
  const totalSteps = state.playType === 'live' ? 5 : 6;
  const stepTitles = state.playType === 'live' ? STEP_TITLES_LIVE : STEP_TITLES_RECORD;

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        const [playersData] = await Promise.all([
          DefaultService.getPlayersPlayersGet(),
          fetchGames(),
          fetchLeagues(),
        ]);
        setPlayers(playersData);
      } catch (err) {
        console.error('Error loading wizard data:', err);
        dispatch({ type: 'SET_ERROR', error: 'Failed to load data. Please try again.' });
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select the current player when data loads
  useEffect(() => {
    if (currentPlayerId && players.length > 0 && !state.selectedPlayerIds.includes(currentPlayerId)) {
      dispatch({ type: 'TOGGLE_PLAYER', playerId: currentPlayerId });
    }
  }, [currentPlayerId, players]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch game-specific leagues when selectedGameId changes
  useEffect(() => {
    if (!state.selectedGameId) {
      setGameSpecificLeagues([]);
      return;
    }
    DefaultService.getLeaguesLeaguesGet({ includeGameSpecific: true })
      .then((allLeagues) => {
        const gameLeagues = allLeagues.filter(
          l => l.isGameSpecificLeague && l.gameId === state.selectedGameId
        );
        setGameSpecificLeagues(gameLeagues);
      })
      .catch((err) => {
        console.error('Failed to fetch game-specific leagues:', err);
      });
  }, [state.selectedGameId]);

  // Auto-select Global + game-specific league when leagues load if none selected
  useEffect(() => {
    if (leagues.length > 0 && state.selectedLeagueIds.length === 0) {
      const globalLeague = leagues.find(l => l.isGlobal);
      const gameLeague = gameSpecificLeagues[0];
      const ids = [globalLeague?.id, gameLeague?.id].filter(Boolean) as string[];
      if (ids.length > 0) {
        dispatch({ type: 'SET_LEAGUE_IDS', leagueIds: ids });
      }
    }
  }, [leagues, gameSpecificLeagues]); // eslint-disable-line react-hooks/exhaustive-deps

  // When game-specific league loads and we're in ranked mode, add it to selection
  useEffect(() => {
    if (gameSpecificLeagues.length === 0 || state.selectedLeagueIds.length === 0) return;
    const unranked = state.selectedLeagueIds.some(id => {
      const l = leagues.find(le => le.id === id);
      return l?.name === 'Unranked';
    });
    if (unranked) return;

    const gameLeagueId = gameSpecificLeagues[0]?.id;
    if (gameLeagueId && !state.selectedLeagueIds.includes(gameLeagueId)) {
      dispatch({ type: 'SET_LEAGUE_IDS', leagueIds: [...state.selectedLeagueIds, gameLeagueId] });
    }
  }, [gameSpecificLeagues]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load live play data when fromLivePlay param is present
  useEffect(() => {
    if (!fromLivePlayId || loading || players.length === 0 || games.length === 0) return;

    const loadLivePlay = async () => {
      try {
        const livePlayData = await LivePlaysService.getLivePlayLivePlaysLivePlayIdGet({
          livePlayId: fromLivePlayId,
        });
        setLivePlay(livePlayData);

        const allPlayerIds: string[] = [];
        const teamsData: { id: string; name: string; color: string; playerIds: string[] }[] = [];
        const teamColors = ['red', 'blue', 'yellow', 'green', 'purple', 'pink'];
        let colorIndex = 0;

        const entities: RankingEntity[] = [];
        let position = 1;

        Object.entries(livePlayData.teams).forEach(([teamName, playerIds]) => {
          allPlayerIds.push(...playerIds);
          if (playerIds.length > 1) {
            const teamId = `team-${playerIds.join('-')}`;
            teamsData.push({
              id: teamId,
              name: teamName,
              color: teamColors[colorIndex % teamColors.length],
              playerIds,
            });
            entities.push({ id: teamId, type: 'team', position, tiedWith: [] });
            colorIndex++;
          } else {
            entities.push({ id: playerIds[0], type: 'player', position, tiedWith: [] });
          }
          position++;
        });

        // Calculate duration from startedAt
        let duration: number | null = null;
        if (livePlayData.startedAt) {
          const diffMs = Date.now() - new Date(livePlayData.startedAt).getTime();
          duration = Math.floor(diffMs / 60000);
        }

        dispatch({
          type: 'INIT_FROM_LIVE_PLAY',
          gameId: livePlayData.gameId,
          gameExtensions: livePlayData.gameExtensions || [],
          leagueIds: livePlayData.leagueIds || [],
          playerIds: allPlayerIds,
          teams: teamsData,
          playDate: livePlayData.startedAt || '',
          durationMinutes: duration,
          teamMetadata: livePlayData.currentState || {},
          fromLivePlayId,
          rankingEntities: entities,
        });
      } catch (err: unknown) {
        console.error('Error loading live play:', err);
        const status = (err as { status?: number }).status;
        if (status === 404) {
          dispatch({ type: 'SET_ERROR', error: 'Live play not found. It may have been cancelled or already ended.' });
        } else {
          dispatch({ type: 'SET_ERROR', error: 'Failed to load live play. Please try again.' });
        }
      }
    };

    loadLivePlay();
  }, [fromLivePlayId, players.length, games.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load rematch data
  useEffect(() => {
    if (!rematchPlayId || loading || players.length === 0 || games.length === 0) return;

    const loadRematchData = async () => {
      try {
        const playData = await DefaultService.getPlayPlaysPlayIdGet({ playId: rematchPlayId });

        const allPlayerIds: string[] = [];
        const teamsData: { id: string; name: string; color: string; playerIds: string[] }[] = [];
        const teamColors = ['red', 'blue', 'yellow', 'green', 'purple', 'pink'];
        let colorIndex = 0;

        Object.entries(playData.teams).forEach(([teamName, playerIds]) => {
          allPlayerIds.push(...playerIds);
          if (playerIds.length > 1) {
            teamsData.push({
              id: `team-${playerIds.join('-')}`,
              name: teamName,
              color: teamColors[colorIndex % teamColors.length],
              playerIds,
            });
            colorIndex++;
          }
        });

        dispatch({
          type: 'INIT_FROM_REMATCH',
          gameId: playData.gameId,
          gameExtensions: playData.gameExtensions || [],
          leagueIds: playData.leagueIds || [],
          playerIds: allPlayerIds,
          teams: teamsData,
        });
      } catch (err: unknown) {
        console.error('Error loading rematch data:', err);
        const status = (err as { status?: number }).status;
        if (status === 404) {
          dispatch({ type: 'SET_ERROR', error: 'Original play not found for rematch.' });
        } else {
          dispatch({ type: 'SET_ERROR', error: 'Failed to load rematch data. Please try again.' });
        }
      }
    };

    loadRematchData();
  }, [rematchPlayId, players.length, games.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize ranking entities when entering step 6 (record path)
  useEffect(() => {
    if (state.playType === 'record' && state.currentStep === 6 && state.rankingEntities.length === 0) {
      const entities = updateRankingEntities(state.teams, state.selectedPlayerIds);
      dispatch({ type: 'SET_RANKING_ENTITIES', entities });
    }
  }, [state.currentStep, state.playType]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Handlers ---

  const handleSelectPlayType = useCallback((playType: 'live' | 'record') => {
    dispatch({ type: 'SET_PLAY_TYPE', playType });
    dispatch({ type: 'NEXT_STEP' });
  }, [dispatch]);

  const handleNext = useCallback(() => {
    if (!isStepValid(state.currentStep, selectedGame)) {
      const messages: Record<number, string> = {
        1: 'Please choose a play type.',
        2: 'Please select a game.',
        3: 'Select at least 2 players.',
        4: 'Please check your team count.',
        5: 'Please select at least one league.',
        6: 'Please set the ranking.',
      };
      dispatch({ type: 'SET_ERROR', error: messages[state.currentStep] || 'Please complete this step.' });
      return;
    }
    dispatch({ type: 'NEXT_STEP' });
  }, [state.currentStep, selectedGame, isStepValid, dispatch]);

  const handlePrev = useCallback(() => {
    if (state.currentStep === 1) {
      clearStorage();
      onClose();
    } else {
      dispatch({ type: 'PREV_STEP' });
    }
  }, [state.currentStep, clearStorage, onClose, dispatch]);

  const handleGoToStep = useCallback((step: number) => {
    dispatch({ type: 'GO_TO_STEP', step });
  }, [dispatch]);

  const handleNewPlayer = useCallback(() => {
    navigate('/players/new', { state: { returnPath: '/plays/new' } });
  }, [navigate]);

  // Convert wizard teams to API format
  const convertTeamsToApiFormat = useCallback((): Record<string, string[]> => {
    const playTeams: Record<string, string[]> = {};
    const usedNames = new Set<string>();

    state.teams.forEach(team => {
      if (team.playerIds.length > 0) {
        let teamName = team.name;
        let counter = 1;
        while (usedNames.has(teamName)) {
          teamName = `${team.name} ${counter}`;
          counter++;
        }
        usedNames.add(teamName);
        playTeams[teamName] = team.playerIds;
      }
    });

    state.selectedPlayerIds.forEach(playerId => {
      const isInTeam = state.teams.some(t => t.playerIds.includes(playerId));
      if (!isInTeam) {
        const player = players.find(p => p.id === playerId);
        if (player) {
          let name = player.name;
          let counter = 1;
          while (usedNames.has(name)) {
            name = `${player.name} ${counter}`;
            counter++;
          }
          usedNames.add(name);
          playTeams[name] = [playerId];
        }
      }
    });

    return playTeams;
  }, [state.teams, state.selectedPlayerIds, players]);

  // Convert ranking entities to API ranking format
  const convertRankingToApiFormat = useCallback((
    rankingEntities: RankingEntity[],
    playTeams: Record<string, string[]>,
  ): (string | string[])[] => {
    const sortedEntities = [...rankingEntities].sort((a, b) => a.position - b.position);

    // Build reverse mapping from entity ID to team name used in playTeams
    const idToName: Record<string, string> = {};
    Object.entries(playTeams).forEach(([usedName, playerIds]) => {
      if (playerIds.length === 1) {
        idToName[playerIds[0]] = usedName;
      } else {
        const team = state.teams.find(t =>
          t.playerIds.length === playerIds.length &&
          t.playerIds.every(id => playerIds.includes(id))
        );
        if (team) {
          idToName[team.id] = usedName;
        }
      }
    });

    const ranking: (string | string[])[] = [];
    const processedPositions = new Set<number>();

    for (const entity of sortedEntities) {
      if (processedPositions.has(entity.position)) continue;

      const entitiesAtPosition = sortedEntities.filter(e => e.position === entity.position);
      const namesAtPosition = entitiesAtPosition
        .map(e => idToName[e.id])
        .filter((name, index, arr) => name && arr.indexOf(name) === index);

      if (namesAtPosition.length === 1) {
        ranking.push(namesAtPosition[0]);
      } else if (namesAtPosition.length > 1) {
        ranking.push(namesAtPosition);
      }

      processedPositions.add(entity.position);
    }

    return ranking;
  }, [state.teams]);

  // Get team display names for metadata
  const teamDisplayNames = useMemo((): string[] => {
    const names: string[] = [];
    state.teams.forEach(team => {
      if (team.playerIds.length > 0) names.push(team.name);
    });
    state.selectedPlayerIds.forEach(playerId => {
      const isInTeam = state.teams.some(t => t.playerIds.includes(playerId));
      if (!isInTeam) {
        const player = players.find(p => p.id === playerId);
        if (player) names.push(player.name);
      }
    });
    return names;
  }, [state.teams, state.selectedPlayerIds, players]);

  const handleTeamMetadataChange = useCallback((metadata: Record<string, Record<string, any>>) => {
    dispatch({ type: 'SET_TEAM_METADATA', metadata });
  }, [dispatch]);

  // --- Submission ---

  const handleSubmit = useCallback(async () => {
    if (state.isSubmitting || submissionRef.current) return;

    dispatch({ type: 'SET_SUBMITTING', value: true });
    dispatch({ type: 'SET_ERROR', error: null });

    try {
      if (state.playType === 'live' && !state.fromLivePlayId) {
        // --- Create live play ---
        const playTeams = convertTeamsToApiFormat();
        const input: LivePlayInput = {
          gameId: state.selectedGameId,
          gameExtensions: state.gameExtensions.length > 0 ? state.gameExtensions : undefined,
          leagueIds: state.selectedLeagueIds,
          teams: playTeams,
        };

        const livePlayResult = await LivePlaysService.createLivePlayLivePlaysPost({
          requestBody: input,
        });

        clearStorage();
        navigate(`/live-plays/${livePlayResult.id}`);
      } else if (state.fromLivePlayId && livePlay) {
        // --- End live play ---
        const livePlayIdToName: Record<string, string> = {};
        Object.entries(livePlay.teams).forEach(([teamName, playerIds]) => {
          if (playerIds.length === 1) {
            livePlayIdToName[playerIds[0]] = teamName;
          } else {
            const teamId = `team-${playerIds.join('-')}`;
            livePlayIdToName[teamId] = teamName;
          }
        });

        const sortedEntities = [...state.rankingEntities].sort((a, b) => a.position - b.position);
        const livePlayRanking: (string | string[])[] = [];
        const processedPositions = new Set<number>();
        for (const entity of sortedEntities) {
          if (processedPositions.has(entity.position)) continue;
          const entitiesAtPosition = sortedEntities.filter(e => e.position === entity.position);
          const namesAtPosition = entitiesAtPosition
            .map(e => livePlayIdToName[e.id])
            .filter((name, index, arr) => name && arr.indexOf(name) === index);

          if (namesAtPosition.length === 1) {
            livePlayRanking.push(namesAtPosition[0]);
          } else if (namesAtPosition.length > 1) {
            livePlayRanking.push(namesAtPosition);
          }
          processedPositions.add(entity.position);
        }

        // Map team metadata using live play team names
        const mappedMetadata: Record<string, Record<string, unknown>> = {};
        Object.entries(state.teamMetadata).forEach(([metadataTeamName, metadata]) => {
          if (livePlay.teams[metadataTeamName]) {
            mappedMetadata[metadataTeamName] = metadata;
          }
        });

        const endInput: LivePlayEndInput = {
          ranking: livePlayRanking,
          comment: state.comment.trim() || undefined,
          durationMinutes: state.durationMinutes || undefined,
          teamMetadata: Object.keys(mappedMetadata).length > 0 ? mappedMetadata : undefined,
        };

        const submissionPromise = LivePlaysService.endLivePlayLivePlaysLivePlayIdEndPost({
          livePlayId: state.fromLivePlayId,
          requestBody: endInput,
        });
        submissionRef.current = submissionPromise;
        const createdPlay = await submissionPromise;
        trackAction('play_recorded', { from_live_play: true, players: state.selectedPlayerIds.length });

        if (state.playImages.length > 0) {
          await DefaultService.uploadPlayImagesPlaysPlayIdImagesPost({
            playId: createdPlay.id,
            formData: { images: state.playImages },
          });
        }

        clearStorage();
        navigate(`/plays/${createdPlay.id}/recap`);
      } else {
        // --- Record new play ---
        const playTeams = convertTeamsToApiFormat();
        const ranking = convertRankingToApiFormat(state.rankingEntities, playTeams);
        const formattedPlayDate = state.playDate ? new Date(state.playDate).toISOString() : undefined;

        // Map team metadata
        const mappedMetadata: Record<string, Record<string, unknown>> = {};
        Object.entries(state.teamMetadata).forEach(([metadataTeamName, metadata]) => {
          const actualTeamName = Object.keys(playTeams).find(teamName => {
            const teamPlayers = playTeams[teamName];
            if (teamPlayers.length > 1) {
              const team = state.teams.find(t => t.name === metadataTeamName);
              return team && team.playerIds.every(id => teamPlayers.includes(id));
            } else {
              const player = players.find(p => p.id === teamPlayers[0]);
              return player && player.name === metadataTeamName;
            }
          });
          if (actualTeamName) {
            mappedMetadata[actualTeamName] = metadata;
          }
        });

        const playInput: PlayInput = {
          gameId: state.selectedGameId,
          gameExtensions: state.gameExtensions.length > 0 ? state.gameExtensions : undefined,
          playDate: formattedPlayDate,
          teams: playTeams,
          ranking,
          comment: state.comment.trim() || undefined,
          leagueIds: state.selectedLeagueIds,
          durationMinutes: state.durationMinutes ?? undefined,
          teamMetadata: Object.keys(mappedMetadata).length > 0 ? mappedMetadata : undefined,
        };

        const submissionPromise = DefaultService.createPlayPlaysPost({ requestBody: playInput });
        submissionRef.current = submissionPromise;
        const createdPlay = await submissionPromise;
        trackAction('play_recorded', { game_id: playInput.gameId, players: state.selectedPlayerIds.length, leagues: state.selectedLeagueIds.length });

        if (state.playImages.length > 0) {
          await DefaultService.uploadPlayImagesPlaysPlayIdImagesPost({
            playId: createdPlay.id,
            formData: { images: state.playImages },
          });
        }

        clearStorage();
        navigate(`/plays/${createdPlay.id}/recap`);
      }
    } catch (err: unknown) {
      console.error('Error submitting:', err);
      const status = (err as { status?: number }).status;
      if (state.fromLivePlayId) {
        if (status === 403) {
          dispatch({ type: 'SET_ERROR', error: 'Only participants can end a live play.' });
        } else if (status === 400) {
          dispatch({ type: 'SET_ERROR', error: 'Invalid data. Please check your ranking and try again.' });
        } else {
          dispatch({ type: 'SET_ERROR', error: 'Failed to end live play. Please try again.' });
        }
      } else if (state.playType === 'live') {
        if (status === 400) {
          dispatch({ type: 'SET_ERROR', error: 'Invalid game or players. Please check your selection.' });
        } else {
          dispatch({ type: 'SET_ERROR', error: 'Failed to start live play. Please try again.' });
        }
      } else {
        dispatch({ type: 'SET_ERROR', error: 'Failed to create play session. Please try again.' });
      }
      dispatch({ type: 'SET_SUBMITTING', value: false });
      submissionRef.current = null;
    }
  }, [state, livePlay, players, convertTeamsToApiFormat, convertRankingToApiFormat, clearStorage, navigate, dispatch]);

  const handleRankingEntitiesChange = useCallback((updater: (prev: RankingEntity[]) => RankingEntity[]) => {
    dispatch({ type: 'UPDATE_RANKING_ENTITIES', updater });
  }, [dispatch]);

  const currentStepValid = isStepValid(state.currentStep, selectedGame);
  const submitLabel = state.playType === 'live' ? 'Start' : (state.fromLivePlayId ? 'End Play' : 'Record');

  // --- Loading ---
  if (loading) {
    return (
      <div className="fixed inset-0 z-[60] flex flex-col sm:flex sm:items-center sm:justify-center sm:bg-black/30" role="dialog" aria-label="Play Wizard" aria-modal="true">
        <div className="flex flex-col w-full h-full sm:max-w-lg sm:max-h-[85vh] sm:rounded-2xl sm:shadow-2xl bg-surface-base sm:overflow-hidden">
          <header className="flex items-center justify-between px-4 h-14 bg-[var(--nav-frosted)] backdrop-blur-md border-b border-[var(--nav-border)] shrink-0">
            <div className="w-20" />
            <span className="text-base font-serif font-semibold text-heading">Loading...</span>
            <div className="w-20" />
          </header>
          <div className="flex-1 flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-stone-200 border-t-primary-500 rounded-full animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  // --- Render ---
  return (
    <div className="fixed inset-0 z-[60] flex flex-col sm:flex sm:items-center sm:justify-center sm:bg-black/30" role="dialog" aria-label="Play Wizard" aria-modal="true">
      <div className="flex flex-col w-full h-full sm:max-w-lg sm:max-h-[85vh] sm:rounded-2xl sm:shadow-2xl bg-surface-base sm:overflow-hidden">
      {/* Header */}
      <header className="flex items-center justify-between px-4 h-14 bg-[var(--nav-frosted)] backdrop-blur-md border-b border-[var(--nav-border)] shrink-0">
        <button
          onClick={() => { clearStorage(); onClose(); }}
          className="flex items-center justify-center text-body hover:text-heading
                     min-h-[48px] min-w-[48px] -ml-2"
          aria-label="Close wizard"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>

        <h1 className="text-base font-serif font-semibold text-heading">
          {stepTitles[state.currentStep] || ''}
        </h1>

        <span className="text-sm text-muted tabular-nums font-mono min-w-[48px] text-right">
          {state.currentStep}/{totalSteps}
        </span>
      </header>

      {/* Error banner */}
      {state.error && (
        <div className="flex items-center gap-2 px-4 py-2 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 shrink-0"
             role="alert">
          <svg className="w-4 h-4 text-red-500 shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <span className="flex-1 text-sm text-red-800 dark:text-red-200">{state.error}</span>
          <button
            onClick={() => dispatch({ type: 'SET_ERROR', error: null })}
            className="shrink-0 p-1 text-red-400 hover:text-red-600 rounded"
            aria-label="Dismiss error"
          >
            <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      )}

      {/* Screen reader announcement */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {`Step ${state.currentStep} of ${totalSteps}: ${stepTitles[state.currentStep] || ''}`}
      </div>

      {/* Step content */}
      <WizardStepContainer
        currentStep={state.currentStep}
        canGoNext={currentStepValid}
        onNext={handleNext}
        onPrev={handlePrev}
      >
        {/* Step 1: Play Type */}
        <PlayTypeStep onSelectType={handleSelectPlayType} />

        {/* Step 2: Game Selection */}
        <GameSelectionStep
          recentGames={recentGames}
          recentGamesLoading={recentGamesLoading}
          allGames={games}
          selectedGameId={state.selectedGameId}
          selectedGame={selectedGame}
          gameExtensions={state.gameExtensions}
          onSelectGame={(gameId) => dispatch({ type: 'SELECT_GAME', gameId })}
          onToggleExtension={(ext) => dispatch({ type: 'TOGGLE_EXTENSION', extension: ext })}
        />

        {/* Step 3: Player Selection */}
        <PlayerSelectionStep
          players={players}
          selectedPlayerIds={state.selectedPlayerIds}
          teams={state.teams}
          recentPlayerIds={recentPlayerIds}
          currentPlayerId={state.playType === 'live' ? currentPlayerId : null}
          leagues={leagues}
          onTogglePlayer={(id) => {
            if (id === currentPlayerId && state.playType === 'live') return;
            dispatch({ type: 'TOGGLE_PLAYER', playerId: id });
          }}
          onCycleTeam={(id) => dispatch({ type: 'CYCLE_TEAM', playerId: id })}
          onNewPlayer={handleNewPlayer}
        />

        {/* Step 4: Team Assignment */}
        <TeamAssignmentStep
          selectedPlayerIds={state.selectedPlayerIds}
          players={players}
          teams={state.teams}
          onCycleTeam={(id) => dispatch({ type: 'CYCLE_TEAM', playerId: id })}
        />

        {/* Step 5: Review & Leagues */}
        <ConfirmStartStep
          selectedGame={selectedGame}
          gameExtensions={state.gameExtensions}
          selectedPlayerIds={state.selectedPlayerIds}
          players={players}
          teams={state.teams}
          leagues={leagues}
          selectedLeagueIds={state.selectedLeagueIds}
          onToggleLeague={(id) => dispatch({ type: 'TOGGLE_LEAGUE', leagueId: id, leagues })}
          onSetLeagueIds={(ids) => dispatch({ type: 'SET_LEAGUE_IDS', leagueIds: ids })}
          gameSpecificLeagues={gameSpecificLeagues}
        />

        {/* Step 6: Ranking + Details (record path only) */}
        {state.playType !== 'live' && (
          <ResultsStepContent
            state={state}
            dispatch={dispatch}
            players={players}
            selectedGame={selectedGame}
            teamDisplayNames={teamDisplayNames}
            handleRankingEntitiesChange={handleRankingEntitiesChange}
            handleTeamMetadataChange={handleTeamMetadataChange}
          />
        )}
      </WizardStepContainer>

      {/* Bottom navigation */}
      <WizardNavBar
        currentStep={state.currentStep}
        totalSteps={totalSteps}
        submitLabel={submitLabel}
        isCurrentStepValid={currentStepValid}
        isSubmitting={state.isSubmitting}
        onBack={handlePrev}
        onNext={handleNext}
        onSubmit={handleSubmit}
        onGoToStep={handleGoToStep}
      />
      </div>
    </div>
  );
};

export default PlayWizard;
